import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Calculator, Loader2, RefreshCw, Download,
  ShieldCheck, Unlock, CheckCircle2,
} from 'lucide-react';
import { CalcRow as Row, money } from './TaxReturnControls';

/**
 * Right-column sidebar on the tax return page: calculation summary, quick actions, sign-off.
 */
const TaxCalculationSidebar = ({
  data, calc, calculating, saving,
  refreshing, downloading,
  signOffAmount, setSignOffAmount, signingOff, reopening,
  isLocked,
  saveReturn, refreshAppData, downloadPdf, signOff, reopenReturn,
}) => (
  <div className="space-y-4">
    <Card className="bg-slate-800 border-slate-700 sticky top-4">
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-white text-sm flex items-center gap-2">
          <Calculator className="h-4 w-4 text-amber-400" />
          Tax Calculation
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        {!calc ? (
          <div className="text-center py-6">
            <p className="text-slate-500 text-sm mb-3">Save the form to calculate</p>
            <Button size="sm" onClick={saveReturn} disabled={saving}
              className="bg-sky-600 hover:bg-sky-700 h-8 text-xs" data-testid="calc-save-btn">
              <Calculator className="h-3 w-3 mr-1" />Save & Calculate
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Income Summary */}
            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/40 space-y-1.5">
              <p className="text-[10px] text-slate-500 uppercase font-medium">Income</p>
              <Row label="Employment (gross)" value={calc.gross_employment} />
              {calc.sed_deduction > 0 && <Row label="SED deduction" value={-calc.sed_deduction} negative />}
              {calc.self_employment_income > 0 && <Row label="Self-employment" value={calc.self_employment_income} />}
              {calc.property_income > 0 && <Row label="Property" value={calc.property_income} />}
              {calc.dividend_income > 0 && <Row label="Dividends" value={calc.dividend_income} />}
              {calc.savings_interest > 0 && <Row label="Savings" value={calc.savings_interest} />}
              {calc.pension_income > 0 && <Row label="Pension" value={calc.pension_income} />}
              {calc.other_income > 0 && <Row label="Other" value={calc.other_income} />}
              <div className="border-t border-slate-700 pt-1.5 mt-1.5">
                <Row label="Total income" value={calc.total_income} bold />
              </div>
            </div>

            {/* Allowances */}
            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/40 space-y-1.5">
              <p className="text-[10px] text-slate-500 uppercase font-medium">Allowances</p>
              <Row label="Personal allowance" value={calc.personal_allowance} />
              <Row label="Savings allowance" value={calc.savings_allowance} />
              <Row label="Dividend allowance" value={calc.dividend_allowance} />
            </div>

            {/* Tax */}
            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/40 space-y-1.5">
              <p className="text-[10px] text-slate-500 uppercase font-medium">Tax Due</p>
              <Row label={`Income tax (${calc.rates_used?.basic_rate}/${calc.rates_used?.higher_rate})`} value={calc.total_income_tax} />
              {calc.ni_class4 > 0 && <Row label="NI Class 4" value={calc.ni_class4} />}
              {calc.capital_gains_tax > 0 && <Row label="Capital gains tax" value={calc.capital_gains_tax} />}
              <div className="border-t border-slate-700 pt-2 mt-2">
                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold text-sm">Total Tax Liability</span>
                  <span className={`font-bold text-base ${calc.total_tax_liability > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {money(calc.total_tax_liability)}
                  </span>
                </div>
              </div>
            </div>

            {/* Tax Paid & Refund */}
            {(calc.total_already_paid > 0 || calc.refund_due !== 0) && (
              <div className={`rounded-lg p-3 border space-y-1.5 ${
                calc.refund_due > 0
                  ? 'bg-emerald-500/10 border-emerald-500/30'
                  : calc.refund_due < 0
                    ? 'bg-red-500/10 border-red-500/30'
                    : 'bg-slate-900/50 border-slate-700/40'
              }`}>
                <p className="text-[10px] text-slate-500 uppercase font-medium">Tax Paid & Refund</p>
                {calc.paye_tax_paid > 0 && <Row label="PAYE deducted" value={calc.paye_tax_paid} />}
                {calc.tax_deducted_at_source > 0 && <Row label="Tax at source" value={calc.tax_deducted_at_source} />}
                {calc.payments_on_account > 0 && <Row label="Payments on account" value={calc.payments_on_account} />}
                <Row label="Total paid" value={calc.total_already_paid} bold />
                <div className="border-t border-slate-700/50 pt-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-bold text-sm">
                      {calc.refund_due >= 0 ? 'Refund Due' : 'Still Owed'}
                    </span>
                    <span className={`font-bold text-lg ${calc.refund_due >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {calc.refund_due >= 0 ? '' : '-'}{money(Math.abs(calc.refund_due))}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {calculating && (
              <div className="flex items-center justify-center gap-2 text-sky-400 text-xs py-1">
                <Loader2 className="h-3 w-3 animate-spin" />Recalculating...
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>

    {/* Quick actions */}
    <Card className="bg-slate-800/50 border-slate-700">
      <CardContent className="p-3 space-y-2">
        <p className="text-[10px] text-slate-500 uppercase font-medium">Quick Actions</p>
        <Button variant="outline" size="sm" onClick={refreshAppData} disabled={refreshing || isLocked}
          className="w-full border-slate-600 text-slate-300 h-8 text-xs justify-start" data-testid="sidebar-refresh">
          <RefreshCw className={`h-3 w-3 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh from Seafarer App
        </Button>
        <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading}
          className="w-full border-slate-600 text-slate-300 h-8 text-xs justify-start" data-testid="sidebar-pdf">
          <Download className="h-3 w-3 mr-2" />
          Download SA100 PDF
        </Button>
      </CardContent>
    </Card>

    {/* Sign-Off Section */}
    <Card className={`border ${isLocked ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-slate-800 border-slate-700'}`}>
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-white text-sm flex items-center gap-2">
          <ShieldCheck className={`h-4 w-4 ${isLocked ? 'text-emerald-400' : 'text-amber-400'}`} />
          {isLocked ? 'Signed Off' : 'Sign Off Tax Return'}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        {isLocked ? (
          <div className="space-y-3" data-testid="signed-off-details">
            <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
              <p className="text-[10px] text-slate-500 uppercase font-medium mb-1">Actual Refund Received</p>
              <p className="text-emerald-400 text-xl font-bold" data-testid="signed-off-actual-refund">
                £{parseFloat(data.actual_refund_received || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
              {calc?.refund_due != null && parseFloat(data.actual_refund_received) !== calc.refund_due && (
                <p className="text-[10px] text-slate-500 mt-1">
                  Estimated was: £{parseFloat(calc.refund_due || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </p>
              )}
            </div>
            {data.signed_off_at && (
              <p className="text-[10px] text-slate-500">
                <CheckCircle2 className="h-3 w-3 inline mr-0.5 text-emerald-400" />
                Signed off on {new Date(data.signed_off_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}
            <Button variant="outline" size="sm" onClick={reopenReturn} disabled={reopening}
              className="w-full border-amber-500/30 text-amber-400 hover:text-amber-300 h-8 text-xs" data-testid="sidebar-reopen">
              {reopening ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Unlock className="h-3 w-3 mr-1" />}
              Reopen for Amendments
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="sign-off-form">
            <p className="text-[10px] text-slate-500">
              Once the client has received their refund from HMRC, enter the actual amount and sign off to finalise this tax year.
            </p>
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Actual Refund Received</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                <Input
                  type="number" step="0.01"
                  value={signOffAmount}
                  onChange={e => setSignOffAmount(e.target.value)}
                  placeholder={calc?.refund_due ? calc.refund_due.toFixed(2) : '0.00'}
                  className="bg-slate-900 border-slate-600 text-white h-9 text-sm pl-7"
                  data-testid="sign-off-amount-input"
                />
              </div>
            </div>
            <Button size="sm" onClick={signOff} disabled={signingOff || !signOffAmount || !calc}
              className="w-full bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid="sign-off-btn">
              {signingOff ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
              Sign Off & Lock Return
            </Button>
            {!calc && (
              <p className="text-[10px] text-amber-400">Save & calculate the return first before signing off.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

export default TaxCalculationSidebar;
